import { useState, useCallback } from 'react';
import API from "../../../../API";
import { emptyTaxForm, COUNTRIES_TAX_CONFIG, validateTaxIdValue } from './../../../../CreatorUser/CreatorPages/Dashboard/editCreator/constants';

export const useTaxManagement = (showSnackbar) => {
  const [taxInfo, setTaxInfo] = useState(null);
  const [taxInfoLoading, setTaxInfoLoading] = useState(false);
  const [taxDialog, setTaxDialog] = useState(false);
  const [newTaxInfo, setNewTaxInfo] = useState(emptyTaxForm());

  const fetchTaxInfo = useCallback(async () => {
    try {
      setTaxInfoLoading(true);
      const response = await API.userWallets.getTaxInfo();
      setTaxInfo(response.tax_info || null);
    } catch (error) {
      console.error("Failed to fetch tax info:", error);
    } finally {
      setTaxInfoLoading(false);
    }
  }, []);

  const handleTaxChange = useCallback((field, value) => {
    setNewTaxInfo(prev => {
      const updated = { ...prev, [field]: value };
      
      // Handle country-specific changes
      if (field === 'country') {
        const countryConfig = COUNTRIES_TAX_CONFIG[value];
        if (countryConfig) {
          // Reset country-specific fields - state codes differ between
          // countries so we always clear it to avoid stale invalid values.
          updated.tax_id_type = '';
          updated.tax_id_number = '';
          updated.state = '';
          updated.w9_submitted = false;
          updated.w8_submitted = false;
          updated.tax_country = value;
        }
      }
      
      return updated;
    });
  }, []);

  const validateTaxInfo = useCallback((taxData) => {
    const errors = [];
    const countryConfig = COUNTRIES_TAX_CONFIG[taxData.country] || COUNTRIES_TAX_CONFIG['US'];

    // Required fields validation
    if (!taxData.legal_name?.trim()) errors.push('Legal name is required');
    if (!taxData.address_line1?.trim()) errors.push('Address is required');
    if (!taxData.city?.trim()) errors.push('City is required');
    if (!taxData.postal_code?.trim()) errors.push('Postal code is required');
    if (!taxData.country) errors.push('Country is required');

    // Country-specific validation
    if (countryConfig.requiresState && !taxData.state?.trim()) {
      errors.push(`${countryConfig.stateLabel || 'State'} is required for ${countryConfig.label}`);
    }

    if (countryConfig.requiresTaxId && !taxData.tax_id_number?.trim()) {
      errors.push(`${countryConfig.taxIdLabel || 'Tax ID'} is required for ${countryConfig.label}`);
    }

    if (countryConfig.requiresTaxIdType && !taxData.tax_id_type) {
      errors.push('Tax ID type is required');
    }

    // Tax ID format validation - country-aware with lenient fallback
    if (taxData.tax_id_number) {
      const isValid = validateTaxIdValue(
        taxData.tax_id_number,
        taxData.tax_id_type,
        taxData.country
      );
      if (!isValid) {
        errors.push(`Invalid ${countryConfig.taxIdLabel || 'Tax ID'} format for ${countryConfig.label}`);
      }
    }

    return errors;
  }, []);

  const handleSaveTaxInfo = useCallback(async () => {
    try {
      // Validate before saving
      const validationErrors = validateTaxInfo(newTaxInfo);
      if (validationErrors.length > 0) {
        showSnackbar(`Please fix the following errors:\n${validationErrors.join('\n')}`, "error");
        return false;
      }

      const response = await API.userWallets.updateTaxInfo(newTaxInfo);
      setTaxInfo(response.tax_info);
      setTaxDialog(false);
      showSnackbar("Tax information saved successfully!");
      return true;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || "Failed to save tax information";
      showSnackbar(errorMessage, "error");
      return false;
    }
  }, [newTaxInfo, showSnackbar, validateTaxInfo]);

  const handleOpenTaxDialog = useCallback(() => {
    if (taxInfo) {
      setNewTaxInfo({
        tax_id_type: taxInfo.tax_id_type || "",
        tax_id_number: taxInfo.tax_id_number || "",
        legal_name: taxInfo.legal_name || "",
        business_name: taxInfo.business_name || "",
        address_line1: taxInfo.address_line1 || "",
        address_line2: taxInfo.address_line2 || "",
        city: taxInfo.city || "",
        state: taxInfo.state || "",
        postal_code: taxInfo.postal_code || "",
        country: taxInfo.country || "US",
        tax_country: taxInfo.tax_country || taxInfo.country || "US",
        w9_submitted: taxInfo.w9_submitted || false,
        w8_submitted: taxInfo.w8_submitted || false,
        backup_withholding: taxInfo.backup_withholding || false,
      });
    } else {
      setNewTaxInfo(emptyTaxForm());
    }
    setTaxDialog(true);
  }, [taxInfo]);

  const handleCloseTaxDialog = useCallback(() => {
    setTaxDialog(false);
    setNewTaxInfo(emptyTaxForm());
  }, []);

  // Check if tax info is required for a wallet type
  const isTaxInfoRequired = useCallback((walletType, country = 'US') => {
    if (walletType === 'paypal') return false;
    
    const countryConfig = COUNTRIES_TAX_CONFIG[country];
    return countryConfig?.requiresTaxForNonPaypal !== false;
  }, []);

  // Check if current tax info is sufficient for a wallet type
  const isTaxInfoSufficient = useCallback((walletType, country = 'US') => {
    if (!isTaxInfoRequired(walletType, country)) return true;
    if (!taxInfo) return false;

    const countryConfig = COUNTRIES_TAX_CONFIG[country];
    if (!countryConfig) return false;

    // Check if all required fields are present
    const hasRequiredFields = taxInfo.legal_name && 
                             taxInfo.address_line1 && 
                             taxInfo.city && 
                             taxInfo.postal_code && 
                             taxInfo.country;

    if (!hasRequiredFields) return false;

    // Check country-specific requirements
    if (countryConfig.requiresState && !taxInfo.state) return false;
    if (countryConfig.requiresTaxId && !taxInfo.tax_id_number) return false;
    if (countryConfig.requiresTaxIdType && !taxInfo.tax_id_type) return false;

    return true;
  }, [taxInfo, isTaxInfoRequired]);

  // Get tax requirements message for a wallet type
  const getTaxRequirementMessage = useCallback((walletType, country = 'US') => {
    if (!isTaxInfoRequired(walletType, country)) {
      return null;
    }

    const countryConfig = COUNTRIES_TAX_CONFIG[country];
    if (!countryConfig) return "Tax information is required for this payment method.";

    return countryConfig.requirementMessage || 
           `Tax information is required for ${walletType} payments in ${countryConfig.label}.`;
  }, [isTaxInfoRequired]);

  return {
    taxInfo,
    taxInfoLoading,
    taxDialog,
    newTaxInfo,
    fetchTaxInfo,
    handleTaxChange,
    handleSaveTaxInfo,
    handleOpenTaxDialog,
    handleCloseTaxDialog,
    validateTaxInfo,
    isTaxInfoRequired,
    isTaxInfoSufficient,
    getTaxRequirementMessage,
  };
};